import { STORAGE_ACCESS_TOKEN } from "@weco/suite-client";

/**
 * JWT-claim-driven module gating (#1342).
 *
 * Reads `rbac_enabled`, `is_super_admin`, `org_id` and `org_scopes` straight
 * from the stored access token. Fail-open contract mirrors the backend's
 * HasModuleAccess: gating only applies when RBAC is rolled out
 * (`rbac_enabled: true`) and the user has org context. Staleness after a role
 * change is bounded by the 15-minute access-token TTL.
 */
export interface ModuleAccessState {
  rbacEnabled: boolean;
  isSuperAdmin: boolean;
  orgId: string | null;
  orgScopes: Record<string, string[]>;
}

const FAIL_OPEN: ModuleAccessState = {
  rbacEnabled: false,
  isSuperAdmin: false,
  orgId: null,
  orgScopes: {},
};

function parseJwt(token: string): Record<string, unknown> | null {
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function readModuleAccess(): ModuleAccessState {
  const token = localStorage.getItem(STORAGE_ACCESS_TOKEN);
  if (!token) return FAIL_OPEN;
  const payload = parseJwt(token);
  if (!payload) return FAIL_OPEN;
  return {
    rbacEnabled: payload.rbac_enabled === true,
    isSuperAdmin: payload.is_super_admin === true,
    orgId: typeof payload.org_id === "string" ? payload.org_id : null,
    orgScopes:
      (payload.org_scopes as Record<string, string[]> | undefined) ?? {},
  };
}

export function canAccessModule(
  state: ModuleAccessState,
  moduleScope?: string,
): boolean {
  if (!moduleScope) return true; // route not gated
  if (!state.rbacEnabled) return true; // fail open pre-rollout
  if (state.isSuperAdmin) return true;
  if (!state.orgId) return true; // no org context
  const scopes = state.orgScopes[state.orgId] ?? [];
  return scopes.includes(moduleScope);
}
